import React, {useEffect, useState} from "react";
import {ArrowLeftOutlined, BankOutlined} from "@ant-design/icons";
import {useNavigate, useParams} from "react-router-dom";
import SharedService from "../../../../services/shared/service";
import {useAuth} from "oidc-react";
import {Button, notification, Spin, Tag, Tooltip} from "antd";
import SEO from "../../../../components/SEO"

const AdminSupplierDetail = () => {
    const pageSEOSettings = {
        title: "Supplier Detail",
    };
    const {userData} = useAuth();
    const token = userData?.access_token;
    const {id} = useParams();
    const navigate = useNavigate();
    const [supplierDetails, setSupplierDetails] = useState(null);
    const [bankDetails, setBankDetails] = useState([]);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        fetchSupplierBySupplierId();
    }, [id]);

    const handleArrowClick = () => {
        navigate("/admin/suppliers");
    };

    const fetchSupplierBySupplierId = async () => {
        setLoading(true);
        try {
            const response = await SharedService.get({
                token,
                url: `supplier/${id}`,
            });

            if (response) {
                const addr = response?.address;
                response.Address = [
                    addr?.address,
                    addr?.town,
                    addr?.state,
                    addr?.postCode,
                    addr?.country,
                ].filter(Boolean).join(", ");
                setSupplierDetails(response);
                setBankDetails(response.bankDetails ?? []);
            }
        } catch (error) {
            if (error.response && error.response.status === 500) {
                notification.error({
                    message: "Error",
                    description: error.response.statusText,
                });
            }
        } finally {
            setLoading(false);
        }
    };

    const Field = ({label, children, span = 1}) => (
        <div className={span === 2 ? "sm:col-span-2" : ""}>
            <div className="text-[11px] uppercase tracking-wider text-[#7A849A] mb-1">{label}</div>
            <div className="text-[14px] text-[#0B1220] break-words">{children || <span className="text-[#A5ADBF]">—</span>}</div>
        </div>
    );

    const BoolTag = ({on}) => (
        <Tag
            className="!m-0"
            color={on ? "green" : "default"}
        >
            {on ? "Yes" : "No"}
        </Tag>
    );

    const statusTags = supplierDetails ? [
        {label: "Vetted", on: supplierDetails.isVetted, color: "green"},
        {label: "Recovery supplier", on: supplierDetails.isRecoverySupplier, color: "geekblue"},
        {label: "Invoice supplier", on: supplierDetails.isCompanyInvoiceSupplier, color: "blue"},
        {label: "Marcas supplier", on: supplierDetails.isMarcasSupplier, color: "purple"},
        {label: "Whitelisted", on: supplierDetails.isFromWhitelist, color: "cyan"},
        {label: "Allows rounding", on: supplierDetails.permitInvoiceRounding, color: "gold"},
    ].filter(t => t.on) : [];

    const recoveryTypes = supplierDetails?.recoveryServiceTypes ?? [];
    const invoiceEmails = supplierDetails?.companyInvoiceEmails ?? [];
    const allowedCurrencies = supplierDetails?.companyAllowedCurrencies ?? [];

    return (
        <div>
            <SEO {...pageSEOSettings} />

            {/* Page header */}
            <div className="px-4 sm:px-7 py-5 relative">
                {loading && (
                    <div className="absolute inset-0 flex justify-center items-center bg-white bg-opacity-50 z-10">
                        <Spin size="large" className="custom-spin"/>
                    </div>
                )}
                <Button
                    size="small"
                    icon={<ArrowLeftOutlined/>}
                    onClick={handleArrowClick}
                    className="mb-2"
                >
                    All suppliers
                </Button>
                <div className="text-[22px] font-semibold text-[#0B1220] leading-tight">Supplier details</div>
                {supplierDetails?.id && (
                    <div className="text-[13px] text-[#7A849A] mt-1 font-mono">{supplierDetails.id}</div>
                )}
            </div>

            <div className="px-4 sm:px-7 pb-7 space-y-4">
                {/* Hero card */}
                {supplierDetails && (
                    <div className="bg-white border border-[#E4E7EE] rounded-[14px] p-5">
                        <div className="flex items-start gap-4 flex-wrap">
                            <div className="flex-1 min-w-0">
                                <div className="text-[18px] font-semibold text-[#0B1220]">
                                    {supplierDetails.name || "—"}
                                </div>
                                {supplierDetails.Address && (
                                    <div className="text-[13px] text-[#7A849A] mt-1">{supplierDetails.Address}</div>
                                )}
                            </div>
                            {statusTags.length > 0 && (
                                <div className="flex flex-wrap gap-1.5">
                                    {statusTags.map((t) => (
                                        <Tag key={t.label} color={t.color} className="!m-0">
                                            {t.label}
                                        </Tag>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>
                )}

                {/* Info grid */}
                {supplierDetails && (
                    <div className="bg-white border border-[#E4E7EE] rounded-[14px]">
                        <div className="px-5 py-3 border-b border-[#F1F3F7]">
                            <div className="text-[15px] font-semibold text-[#0B1220]">Information</div>
                        </div>
                        <div className="p-5 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-5">
                            <Field label="Type">{supplierDetails.type?.[0]}</Field>
                            <Field label="Country">{supplierDetails.address?.countryName}</Field>
                            <Field label="Telephone">{supplierDetails.address?.telephone}</Field>

                            <Field label="Email">{supplierDetails.email}</Field>
                            <Field label="Correspondence email">{supplierDetails.correspondenceEmail}</Field>
                            <Field label="Payment term days">{supplierDetails.paymentTermDays}</Field>

                            <Field label="Allowed currencies" span={2}>
                                {allowedCurrencies.length > 0 ? (
                                    <div className="flex flex-wrap gap-1.5">
                                        {allowedCurrencies.map((c, i) => (
                                            <Tag key={i} className="!m-0 cell-mono">{c.value}</Tag>
                                        ))}
                                    </div>
                                ) : null}
                            </Field>
                            <Field label="Purchase orders LTM">
                                <span className="cell-amt">{supplierDetails.purchasesCount ?? 0}</span>
                            </Field>

                            <Field label="Invoice emails" span={2}>
                                {invoiceEmails.length > 0 ? (
                                    <div className="flex flex-wrap gap-1">
                                        {invoiceEmails.map((e, i) => (
                                            <span key={i} className="cell-mono">{e.value}{i < invoiceEmails.length - 1 ? "," : ""}</span>
                                        ))}
                                    </div>
                                ) : null}
                            </Field>
                            <Field label="Recovery service types">
                                {recoveryTypes.length > 0 ? (
                                    <div className="flex flex-wrap gap-1.5">
                                        {recoveryTypes.map((r, i) => (
                                            <Tag key={i} className="!m-0">{r.value}</Tag>
                                        ))}
                                    </div>
                                ) : null}
                            </Field>
                        </div>

                        {/* Status row — stays even when none of the booleans are true */}
                        <div className="px-5 pb-5 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-x-6 gap-y-3 border-t border-[#F1F3F7] pt-4 mt-1">
                            <Field label="Vetted"><BoolTag on={supplierDetails.isVetted}/></Field>
                            <Field label="Recovery"><BoolTag on={supplierDetails.isRecoverySupplier}/></Field>
                            <Field label="Invoice supplier"><BoolTag on={supplierDetails.isCompanyInvoiceSupplier}/></Field>
                            <Field label="Marcas"><BoolTag on={supplierDetails.isMarcasSupplier}/></Field>
                            <Field label="Whitelisted"><BoolTag on={supplierDetails.isFromWhitelist}/></Field>
                            <Field label="Rounding"><BoolTag on={supplierDetails.permitInvoiceRounding}/></Field>
                        </div>
                    </div>
                )}

                {/* Bank details */}
                {bankDetails.length > 0 && (
                    <div>
                        <div className="flex items-center gap-2 mb-3 px-1">
                            <BankOutlined className="text-[#7A849A]"/>
                            <div className="text-[15px] font-semibold text-[#0B1220]">
                                Bank details
                                <span className="text-[#7A849A] font-normal"> · {bankDetails.length}</span>
                            </div>
                        </div>
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                            {bankDetails.map((item, index) => (
                                <div key={index} className="bg-white border border-[#E4E7EE] rounded-[14px]">
                                    <div className="px-5 py-3 border-b border-[#F1F3F7] flex items-center gap-3">
                                        <div className="text-[15px] font-semibold text-[#0B1220] truncate">
                                            {item.name || "Bank"}
                                        </div>
                                        {item.currencyId && (
                                            <Tag className="!m-0 cell-mono">{item.currencyId}</Tag>
                                        )}
                                        <div className="flex-1"/>
                                        {item.id && (
                                            <Tooltip title={`ID ${item.id}`}>
                                                <span className="cell-sub cell-mono">#{item.id}</span>
                                            </Tooltip>
                                        )}
                                    </div>
                                    <div className="p-5 grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-4">
                                        <Field label="Account number">
                                            <span className="cell-mono">{item.accountNumber}</span>
                                        </Field>
                                        <Field label="Country">{item.countryId}</Field>
                                        <Field label="IBAN">
                                            <span className="cell-mono">{item.iban}</span>
                                        </Field>
                                        <Field label="SWIFT">
                                            <span className="cell-mono">{item.swiftCode}</span>
                                        </Field>
                                        <Field label="IFSC">
                                            <span className="cell-mono">{item.ifsc}</span>
                                        </Field>
                                        <Field label="Address" span={2}>{item.address}</Field>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default AdminSupplierDetail;
